import { useContext, useEffect, useMemo, useState } from "react";

import { Paper, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { MODULE_FINANCIAL } from "components/Menu/config/constants";
import SkeletonTable from "components/shared/table-skeleton/table-skeleton";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";

import useFinanceReport from "../../Financial/useFinanceReport";
import { ReportsIcon } from "../../Icons/Icons";
import Letterhead from "../../Letterhead";
import { formatText, formatTextShort } from "./helpers";

const TOTALS_CONFIG = [
  { labelLeft: "Total Assets", keyLeft: "assets", labelRight: "Total Liabilities", keyRight: "liabilities" },
  {
    labelLeft: "Current Assets",
    keyLeft: "assets_current",
    labelRight: "Current Liabilities",
    keyRight: "liabilities_current",
  },
  {
    labelLeft: "Long-term Assets",
    keyLeft: "assets_noncurrent",
    labelRight: "Long-term Liabilities",
    keyRight: "liabilities_noncurrent",
  },
];

export default function BalanceSheet() {
  const { enqueueSnackbar } = useSnackbar();
  const { globalState, globalDispatch } = useContext(AppContext);
  const [currentYear, setCurrentYear] = useState(globalState.crop_year);

  const { isLoadingBalanceSheet, balanceSheet } = useFinanceReport({
    onError: (err) => enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { variant: "error" }),
    currentYear,
    loadModules: [MODULE_FINANCIAL],
  });

  useEffect(() => {
    if (globalState.crop_year !== currentYear) {
      globalDispatch({ type: "update", payload: { key: "crop_year", value: currentYear } });
    }
  }, [currentYear, globalState.crop_year, globalDispatch]);

  const { assets_container = [], liabilities_container = [], equity, ...balanceData } = balanceSheet || {};

  const tableCells = useMemo(() => {
    const maxLength = Math.max(assets_container.length, liabilities_container.length);
    return Array.from({ length: maxLength }, (_, idx) => [
      assets_container[idx] ? formatTextShort(assets_container[idx]) : "",
      assets_container[idx] ? balanceSheet[assets_container[idx]] || {} : {},
      liabilities_container[idx] ? formatTextShort(liabilities_container[idx]) : "",
      liabilities_container[idx] ? balanceSheet[liabilities_container[idx]] || {} : {},
    ]);
  }, [assets_container, liabilities_container, balanceSheet]);

  const totals = useMemo(() => {
    return TOTALS_CONFIG.map(({ labelLeft, keyLeft, labelRight, keyRight }) => ({
      labelLeft,
      valueLeft: balanceData[keyLeft]?.total,
      labelRight,
      valueRight: balanceData[keyRight]?.total,
    }));
  }, [balanceData]);

  const renderRows = (assetsData, liabilitiesData) => {
    const assetValues = assetsData?.values || [];
    const liabilityValues = liabilitiesData?.values || [];
    const maxLength = Math.max(assetValues.length, liabilityValues.length);

    return Array.from({ length: maxLength }, (_, index) => (
      <TableRow key={index}>
        <TableCell sx={{ pl: 4 }}>{formatText(assetValues[index]?.item)}</TableCell>
        <TableCell align="right">{assetValues[index] ? formatPrice(assetValues[index].value, 0) : ""}</TableCell>
        <TableCell sx={{ pl: 4 }}>{formatText(liabilityValues[index]?.item)}</TableCell>
        <TableCell align="right">
          {liabilityValues[index] ? formatPrice(liabilityValues[index].value, 0) : ""}
        </TableCell>
      </TableRow>
    ));
  };

  const isEmpty = !isLoadingBalanceSheet && (!balanceSheet || !Object.keys(balanceSheet).length);

  return (
    <>
      <div className="no-print">
        <WorkspaceHeader
          title="Balance Sheet Report"
          category={{
            title: "Reports",
            icon: <ReportsIcon />,
          }}
        />

        <WorkspacePageHeader
          title="Year"
          currentYear={currentYear}
          onYearChange={setCurrentYear}
          isLoading={isLoadingBalanceSheet}
        />
      </div>

      {isEmpty ? (
        <EmptyCard text="No search results" secondaryText="Remove some filters or add a new balance sheet" />
      ) : (
        <Paper
          elevation={3}
          className="reportPaper"
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            maxWidth: "1200px",
            width: "fit-content",
            minWidth: "800px",
            "@media print": {
              "&.reportPaper": {
                boxShadow: "none !important",
                border: "none !important",
                borderRadius: "0 !important",
                p: 0,
                backgroundColor: "transparent !important",
              },
            },
          }}
        >
          <div className="show-in-print report-header-with-letterhead">
            <Typography variant="h1" sx={{ mb: 0 }}>
              Balance Sheet Report - Year: {currentYear}
            </Typography>
            <Letterhead />
          </div>

          <Table size="small" className="financial-report-table reportTable">
            {isLoadingBalanceSheet ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4}>
                    <SkeletonTable skeletonTableRows={10} skeletonTableColumn={4} />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={2} width={"50%"}>
                      <Typography fontWeight={"bold"} fontSize={"1.2em"}>
                        Assets
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={2} width={"50%"}>
                      <Typography fontWeight={"bold"} fontSize={"1.2em"}>
                        Liabilities
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>

                {tableCells.map(([leftLabel, leftData, rightLabel, rightData], idx) => (
                  <TableBody key={idx} className="financial-report-group">
                    <TableRow className="highlight-print" sx={{ bgcolor: "grey.200" }}>
                      <TableCell colSpan={2} className="financial-report-table-cell">
                        <Typography>{leftLabel}</Typography>
                      </TableCell>
                      <TableCell colSpan={2} className="financial-report-table-cell">
                        <Typography>{rightLabel}</Typography>
                      </TableCell>
                    </TableRow>
                    {renderRows(leftData, rightData)}
                  </TableBody>
                ))}

                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography fontWeight={"bold"}>Total</Typography>
                    </TableCell>
                  </TableRow>
                  {totals.map(({ labelLeft, valueLeft, labelRight, valueRight }, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ pl: 2, fontWeight: "bold" }}>{labelLeft}</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        {formatPrice(valueLeft, 0)}
                      </TableCell>
                      <TableCell sx={{ pl: 2, fontWeight: "bold" }}>{labelRight}</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        {formatPrice(valueRight, 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableBody
                  sx={{
                    "@media print": {
                      pageBreakInside: "avoid",
                      breakInside: "avoid",
                    },
                  }}
                >
                  <TableRow sx={{ bgcolor: "grey.200" }} className="highlight-print">
                    <TableCell colSpan={4}>
                      <Typography>Equity</Typography>
                    </TableCell>
                  </TableRow>
                  {renderRows(equity, null)}
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography fontWeight={"bold"}>Total</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 2, fontWeight: "bold" }}>Owners' Equity</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="right">
                      {formatPrice(equity?.total, 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </>
            )}
          </Table>
        </Paper>
      )}
    </>
  );
}
